import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { ArrowLeft, X, ChevronLeft, ChevronRight, Link2, ZoomIn, ZoomOut, Maximize2, Play, Pause } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BOOK_COVER = 'https://customer-assets.emergentagent.com/job_8519ff1f-a56c-4ad1-bdd3-9bc47b61a225/artifacts/9dafrk4v_IMG_0654.jpeg';

export default function ProductPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user, setUser } = useAuth();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeImage, setActiveImage] = useState(0);
    // Lightbox zoom state
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [zoom, setZoom] = useState(1);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [dragging, setDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

    // Checkout
    const [checkoutData, setCheckoutData] = useState(null);
    const [checkoutLoading, setCheckoutLoading] = useState(false);
    const [checkoutSuccess, setCheckoutSuccess] = useState(null);
    const [error, setError] = useState('');

    // Auth gate
    const [showAuth, setShowAuth] = useState(false);
    const [authTab, setAuthTab] = useState('login');
    const [loginForm, setLoginForm] = useState({ email: '', password: '' });
    const [registerForm, setRegisterForm] = useState({ full_name: '', email: '', password: '', confirm_password: '' });
    const [authError, setAuthError] = useState('');
    const [authLoading, setAuthLoading] = useState(false);

    useEffect(() => {
        axios.get(`${API}/products/${id}`).then(r => setProduct(r.data)).catch(() => navigate('/')).finally(() => setLoading(false));
    }, [id, navigate]);

    const mainImage = product ? (
        product.display_url
        || (product.cover_derivatives && (product.cover_derivatives.large || product.cover_derivatives.original))
        || product.cover_image_url
        || (product.image_path ? `${API}/files/${product.image_path}` : BOOK_COVER)
    ) : BOOK_COVER;

    const allImages = product ? [
        mainImage,
        ...(product.additional_urls || (product.additional_images || []).map(p => `${API}/files/${p}`))
    ] : [];

    const statusLabel = (type) => type === 'ebook' ? 'E-Book / Digital Download' : type === 'audiobook' ? 'Audiobook · Streaming + Downloads' : 'Physical Book';

    const startCheckout = async (prod) => {
        setCheckoutData(null);
        setCheckoutSuccess(null);
        setError('');
        setCheckoutLoading(true);
        try {
            const r = await axios.post(`${API}/orders/create`, { product_id: prod.product_id }, { withCredentials: true });
            setCheckoutData(r.data);
            if (r.data.mode === 'live' && r.data.approval_url) {
                window.location.href = r.data.approval_url;
                return;
            }
        } catch (e) {
            setError(e.response?.data?.detail || 'Failed to start checkout');
        } finally { setCheckoutLoading(false); }
    };

    const handleBuyNow = () => {
        if (!user) {
            setShowAuth(true);
            setAuthTab('login');
            setAuthError('');
            return;
        }
        startCheckout(product);
    };

    const handleAuthLogin = async (e) => {
        e.preventDefault(); setAuthError(''); setAuthLoading(true);
        try {
            const r = await axios.post(`${API}/auth/login`, loginForm, { withCredentials: true });
            if (r.data.role === 'admin') { window.location.href = '/admin'; return; }
            setUser(r.data);
            setShowAuth(false);
            startCheckout(product);
        } catch (err) { setAuthError(err.response?.data?.detail || 'Login failed'); }
        finally { setAuthLoading(false); }
    };

    const handleAuthRegister = async (e) => {
        e.preventDefault(); setAuthError(''); setAuthLoading(true);
        try {
            const r = await axios.post(`${API}/auth/register`, registerForm, { withCredentials: true });
            setUser(r.data);
            setShowAuth(false);
            startCheckout(product);
        } catch (err) { setAuthError(err.response?.data?.detail || 'Registration failed'); }
        finally { setAuthLoading(false); }
    };

    const handleConfirmPurchase = async () => {
        if (!checkoutData) return;
        setCheckoutLoading(true); setError('');
        try {
            const r = await axios.post(`${API}/orders/capture`, {
                paypal_order_id: checkoutData.paypal_order_id,
                product_id: checkoutData.product_id
            }, { withCredentials: true });
            setCheckoutSuccess(r.data);
        } catch (e) { setError(e.response?.data?.detail || 'Payment processing failed'); }
        finally { setCheckoutLoading(false); }
    };

    const inputCls = "w-full bg-white text-navy border border-gray-300 rounded-sm px-4 py-3 font-body text-base min-h-[44px] focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent";

    if (loading) return (
        <div className="min-h-screen bg-navy flex items-center justify-center">
            <p className="font-body text-gold text-lg">Loading...</p>
        </div>
    );

    if (!product) return null;

    return (
        <div className="min-h-screen bg-ivory" data-testid="product-page">
            {product && (
                <Helmet>
                    <title>{`${product.title} | Know Your Stuff Book — Alice M. White`}</title>
                    <meta name="description" content={product.description || `${product.title} by Alice M. White, B.S., MBA. Available at knowyourstuffbook.com.`} />
                </Helmet>
            )}
            {/* Top Bar */}
            <div className="bg-navy border-b border-gold/30">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <button onClick={() => navigate('/')} className="font-heading text-gold text-xl font-bold tracking-wide hover:opacity-80 transition-opacity" data-testid="product-page-logo">
                        KNOW YOUR STUFF
                    </button>
                    <button onClick={() => navigate('/')} className="flex items-center gap-2 font-body text-white/80 hover:text-gold transition-colors" data-testid="product-page-back-btn">
                        <ArrowLeft size={18} /> Back to Shop
                    </button>
                </div>
            </div>

            {/* Product Content */}
            <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                    {/* Image Gallery */}
                    <div data-testid="product-page-gallery">
                        <div className="bg-navy rounded-lg overflow-hidden flex items-center justify-center aspect-[2/3] relative cursor-zoom-in" onClick={() => { setZoom(1); setPan({x:0,y:0}); setLightboxOpen(true); }}>
                            <img
                                src={allImages[activeImage] || BOOK_COVER}
                                alt={product.title}
                                className="max-w-full max-h-full w-auto h-auto object-contain p-8"
                                data-testid="product-page-main-image"
                            />
                            <div className="absolute bottom-3 right-3 bg-white/90 text-navy rounded-full p-2 shadow pointer-events-none" data-testid="zoom-hint">
                                <Maximize2 size={18}/>
                            </div>
                            {allImages.length > 1 && (
                                <>
                                    <button
                                        onClick={() => setActiveImage(i => (i - 1 + allImages.length) % allImages.length)}
                                        className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-navy rounded-full w-10 h-10 flex items-center justify-center shadow-lg transition-colors"
                                        data-testid="gallery-prev-btn"
                                    >
                                        <ChevronLeft size={20} />
                                    </button>
                                    <button
                                        onClick={() => setActiveImage(i => (i + 1) % allImages.length)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-navy rounded-full w-10 h-10 flex items-center justify-center shadow-lg transition-colors"
                                        data-testid="gallery-next-btn"
                                    >
                                        <ChevronRight size={20} />
                                    </button>
                                </>
                            )}
                        </div>
                        {/* Thumbnails */}
                        {allImages.length > 1 && (
                            <div className="flex gap-3 mt-4 overflow-x-auto pb-2" data-testid="product-page-thumbnails">
                                {allImages.map((src, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setActiveImage(i)}
                                        className={`flex-shrink-0 w-20 h-28 rounded border-2 overflow-hidden transition-all bg-navy/5 flex items-center justify-center ${activeImage === i ? 'border-gold shadow-md' : 'border-gray-200 hover:border-gold/50'}`}
                                        data-testid={`thumbnail-${i}`}
                                    >
                                        <img src={src} alt={`View ${i + 1}`} className="w-full h-full object-contain p-1" loading="lazy" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Product Info */}
                    <div className="flex flex-col" data-testid="product-page-info">
                        <span className={`text-xs font-body font-bold px-3 py-1 rounded-sm w-fit mb-4 ${product.type === 'ebook' ? 'bg-gold text-navy' : product.type === 'audiobook' ? 'bg-navy text-gold' : 'bg-white text-navy border border-navy/20'}`}>
                            {statusLabel(product.type)}
                        </span>
                        <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-navy mb-6" data-testid="product-page-title">{product.title}</h1>

                        {product.description && (
                            <div className="font-body text-navy/70 text-lg leading-relaxed mb-6 whitespace-pre-line" data-testid="product-page-description">
                                {product.description}
                            </div>
                        )}

                        {product.type === 'audiobook' && (
                            <AudiobookPreview productId={product.product_id} />
                        )}

                        {/* Share Links */}
                        <div className="flex items-center gap-4 mb-8 pb-6 border-b border-gray-200">
                            <span className="font-body text-navy/50 text-sm font-semibold">Share this product:</span>
                            <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noopener noreferrer" className="text-navy/40 hover:text-blue-600 transition-colors" data-testid="page-share-fb" title="Share on Facebook">
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                            </a>
                            <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(product.title)}`} target="_blank" rel="noopener noreferrer" className="text-navy/40 hover:text-black transition-colors" data-testid="page-share-x" title="Share on X">
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                            </a>
                            <button onClick={() => navigator.clipboard.writeText(window.location.href)} className="text-navy/40 hover:text-gold transition-colors" data-testid="page-share-link" title="Copy link">
                                <Link2 size={20}/>
                            </button>
                        </div>

                        <div className="bg-white rounded-lg border border-gray-200 p-6 mt-auto">
                            <div className="flex items-center justify-between mb-5">
                                <span className="font-heading text-4xl font-bold text-gold" data-testid="product-page-price">${parseFloat(product.price).toFixed(2)}</span>
                                {product.type === 'ebook' && <span className="font-body text-sm text-navy/50">Instant digital download</span>}
                                {product.type === 'audiobook' && <span className="font-body text-sm text-navy/50">Instant streaming + 100 downloads</span>}
                                {product.type === 'physical' && <span className="font-body text-sm text-navy/50">Ships to your address</span>}
                            </div>
                            <button
                                onClick={handleBuyNow}
                                className="w-full bg-gold hover:bg-gold-hover text-navy font-bold px-8 py-4 rounded-sm font-body text-lg transition-colors min-h-[56px]"
                                data-testid="product-page-buy-btn"
                            >
                                Buy Now
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Lightbox / Zoom Overlay */}
            {lightboxOpen && (
                <div className="fixed inset-0 bg-black/95 z-50 flex flex-col" data-testid="lightbox-overlay">
                    {/* Lightbox Controls */}
                    <div className="flex items-center justify-between px-6 py-4">
                        <div className="flex items-center gap-3">
                            <button onClick={() => setZoom(z => Math.min(z + 0.5, 5))} className="bg-white/10 hover:bg-white/20 text-white rounded-full w-10 h-10 flex items-center justify-center transition-colors" data-testid="lightbox-zoom-in"><ZoomIn size={20}/></button>
                            <button onClick={() => { setZoom(z => Math.max(z - 0.5, 0.5)); setPan({x:0,y:0}); }} className="bg-white/10 hover:bg-white/20 text-white rounded-full w-10 h-10 flex items-center justify-center transition-colors" data-testid="lightbox-zoom-out"><ZoomOut size={20}/></button>
                            <span className="text-white/60 font-body text-sm">{Math.round(zoom * 100)}%</span>
                        </div>
                        <span className="text-white/60 font-body text-sm">{activeImage + 1} / {allImages.length}</span>
                        <button onClick={() => setLightboxOpen(false)} className="bg-white/10 hover:bg-white/20 text-white rounded-full w-10 h-10 flex items-center justify-center transition-colors" data-testid="lightbox-close"><X size={20}/></button>
                    </div>
                    {/* Lightbox Image */}
                    <div
                        className="flex-1 flex items-center justify-center overflow-hidden relative select-none"
                        onMouseDown={(e) => { if (zoom > 1) { setDragging(true); setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y }); } }}
                        onMouseMove={(e) => { if (dragging) setPan({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y }); }}
                        onMouseUp={() => setDragging(false)}
                        onMouseLeave={() => setDragging(false)}
                        onWheel={(e) => {
                            e.preventDefault();
                            setZoom(z => {
                                const next = z + (e.deltaY < 0 ? 0.25 : -0.25);
                                if (next <= 1) setPan({x:0,y:0});
                                return Math.max(0.5, Math.min(5, next));
                            });
                        }}
                        onDoubleClick={() => {
                            if (zoom > 1) { setZoom(1); setPan({x:0,y:0}); }
                            else setZoom(2.5);
                        }}
                        style={{ cursor: zoom > 1 ? (dragging ? 'grabbing' : 'grab') : 'zoom-in' }}
                    >
                        <img
                            src={allImages[activeImage] || BOOK_COVER}
                            alt={product.title}
                            className="max-w-none transition-transform duration-150"
                            style={{ transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)` }}
                            draggable={false}
                            data-testid="lightbox-image"
                        />
                    </div>
                    {/* Lightbox Navigation */}
                    {allImages.length > 1 && (
                        <div className="flex items-center justify-center gap-4 py-4">
                            <button onClick={() => { setActiveImage(i => (i-1+allImages.length) % allImages.length); setZoom(1); setPan({x:0,y:0}); }} className="bg-white/10 hover:bg-white/20 text-white rounded-full w-10 h-10 flex items-center justify-center transition-colors" data-testid="lightbox-prev"><ChevronLeft size={20}/></button>
                            <div className="flex gap-2">
                                {allImages.map((src, i) => (
                                    <button key={i} onClick={() => { setActiveImage(i); setZoom(1); setPan({x:0,y:0}); }} className={`w-14 h-14 rounded border-2 overflow-hidden transition-all ${activeImage === i ? 'border-gold' : 'border-white/20 hover:border-white/50'}`}>
                                        <img src={src} alt={`${i+1}`} className="w-full h-full object-cover"/>
                                    </button>
                                ))}
                            </div>
                            <button onClick={() => { setActiveImage(i => (i+1) % allImages.length); setZoom(1); setPan({x:0,y:0}); }} className="bg-white/10 hover:bg-white/20 text-white rounded-full w-10 h-10 flex items-center justify-center transition-colors" data-testid="lightbox-next"><ChevronRight size={20}/></button>
                        </div>
                    )}
                </div>
            )}

            {/* Auth Modal */}
            {showAuth && (
                <div className="fixed inset-0 bg-navy/80 backdrop-blur flex items-center justify-center z-50 p-4" data-testid="product-page-auth-modal">
                    <div className="bg-white rounded max-w-md w-full p-8 relative max-h-[90vh] overflow-y-auto">
                        <button onClick={() => setShowAuth(false)} className="absolute top-4 right-4 text-navy/40 hover:text-navy"><X size={20} /></button>
                        <h3 className="font-heading text-2xl font-bold text-navy mb-1">Sign in to Purchase</h3>
                        <p className="font-body text-navy/60 text-sm mb-6">
                            Purchasing: <span className="font-semibold text-navy">{product.title}</span> — <span className="text-gold font-bold">${parseFloat(product.price).toFixed(2)}</span>
                        </p>
                        <div className="flex border-b border-gray-200 mb-6">
                            {[['login', 'Login'], ['register', 'Create Account']].map(([t, l]) => (
                                <button key={t} onClick={() => { setAuthTab(t); setAuthError(''); }} className={`font-body font-semibold px-5 py-2 text-base border-b-2 transition-colors min-h-[44px] ${authTab === t ? 'border-gold text-gold' : 'border-transparent text-navy/60 hover:text-navy'}`}>{l}</button>
                            ))}
                        </div>
                        {authTab === 'login' ? (
                            <form onSubmit={handleAuthLogin} className="space-y-4">
                                <div><label className="font-body text-navy/70 text-sm font-semibold block mb-1">Email</label>
                                    <input type="email" required value={loginForm.email} onChange={e => setLoginForm({ ...loginForm, email: e.target.value })} className={inputCls} /></div>
                                <div><label className="font-body text-navy/70 text-sm font-semibold block mb-1">Password</label>
                                    <input type="password" required value={loginForm.password} onChange={e => setLoginForm({ ...loginForm, password: e.target.value })} className={inputCls} /></div>
                                {authError && <p className="text-red-600 text-sm font-body bg-red-50 border border-red-200 rounded p-3">{authError}</p>}
                                <button type="submit" disabled={authLoading} className="w-full bg-gold hover:bg-gold-hover disabled:opacity-50 text-navy font-bold py-3 rounded-sm font-body text-lg transition-colors min-h-[44px]">
                                    {authLoading ? 'Signing in...' : 'Login & Continue to Purchase'}
                                </button>
                                <div className="border-t border-gray-200 pt-4">
                                    <button type="button" onClick={() => {
                                        sessionStorage.setItem('authGateProduct', JSON.stringify(product));
                                        const redirectUrl = window.location.origin + '/';
                                        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                                    }} className="w-full border border-gray-300 text-navy bg-white hover:bg-gray-50 font-body font-semibold py-3 rounded-sm flex items-center justify-center gap-3 transition-colors min-h-[44px]">
                                        <svg width="20" height="20" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" /><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" /><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" /><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" /></svg>
                                        Continue with Google
                                    </button>
                                </div>
                            </form>
                        ) : (
                            <form onSubmit={handleAuthRegister} className="space-y-4">
                                <div><label className="font-body text-navy/70 text-sm font-semibold block mb-1">Full Name</label>
                                    <input type="text" required value={registerForm.full_name} onChange={e => setRegisterForm({ ...registerForm, full_name: e.target.value })} className={inputCls} /></div>
                                <div><label className="font-body text-navy/70 text-sm font-semibold block mb-1">Email</label>
                                    <input type="email" required value={registerForm.email} onChange={e => setRegisterForm({ ...registerForm, email: e.target.value })} className={inputCls} /></div>
                                <div><label className="font-body text-navy/70 text-sm font-semibold block mb-1">Password</label>
                                    <input type="password" required value={registerForm.password} onChange={e => setRegisterForm({ ...registerForm, password: e.target.value })} className={inputCls} placeholder="Minimum 8 characters" /></div>
                                <div><label className="font-body text-navy/70 text-sm font-semibold block mb-1">Confirm Password</label>
                                    <input type="password" required value={registerForm.confirm_password} onChange={e => setRegisterForm({ ...registerForm, confirm_password: e.target.value })} className={inputCls} /></div>
                                {authError && <p className="text-red-600 text-sm font-body bg-red-50 border border-red-200 rounded p-3">{authError}</p>}
                                <button type="submit" disabled={authLoading} className="w-full bg-navy hover:bg-navy/80 disabled:opacity-50 text-white font-bold py-3 rounded-sm font-body text-lg transition-colors min-h-[44px]">
                                    {authLoading ? 'Creating account...' : 'Create Account & Purchase'}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            )}

            {/* Checkout Modal */}
            {checkoutData && !checkoutSuccess && (
                <div className="fixed inset-0 bg-navy/80 backdrop-blur flex items-center justify-center z-50 p-4" data-testid="product-page-checkout-modal">
                    <div className="bg-white rounded max-w-md w-full p-8 relative">
                        <button onClick={() => { setCheckoutData(null); setError(''); }} className="absolute top-4 right-4 text-navy/40 hover:text-navy"><X size={20} /></button>
                        <h3 className="font-heading text-2xl font-bold text-navy mb-2">Complete Your Order</h3>
                        {checkoutData?.mode === 'demo' && (
                            <p className="font-body text-navy/60 text-sm mb-6 bg-ivory/50 border border-gold/30 rounded p-3">
                                Demo checkout — PayPal integration will be activated when credentials are configured in Admin Settings.
                            </p>
                        )}
                        <div className="space-y-3 font-body text-navy mb-6">
                            <div className="flex justify-between"><span className="text-navy/70">Product:</span><span className="font-semibold">{product.title}</span></div>
                            <div className="flex justify-between"><span className="text-navy/70">Type:</span><span>{statusLabel(product.type)}</span></div>
                            <div className="flex justify-between border-t pt-3"><span className="font-bold">Total:</span><span className="font-bold text-gold text-xl">${parseFloat(product.price).toFixed(2)}</span></div>
                        </div>
                        {error && <p className="text-red-600 text-sm font-body mb-4 bg-red-50 border border-red-200 rounded p-3">{error}</p>}
                        <button onClick={handleConfirmPurchase} disabled={checkoutLoading} className="w-full bg-gold hover:bg-gold-hover disabled:opacity-50 text-navy font-bold py-4 rounded-sm font-body text-lg transition-colors min-h-[56px]">
                            {checkoutLoading ? 'Processing...' : checkoutData?.mode === 'demo' ? 'Complete Purchase (Demo)' : 'Complete Purchase'}
                        </button>
                    </div>
                </div>
            )}

            {/* Success Modal */}
            {checkoutSuccess && (
                <div className="fixed inset-0 bg-navy/80 backdrop-blur flex items-center justify-center z-50 p-4" data-testid="product-page-success-modal">
                    <div className="bg-white rounded max-w-md w-full p-8 text-center space-y-4">
                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                        </div>
                        <h3 className="font-heading text-2xl font-bold text-navy">Order Confirmed!</h3>
                        <p className="font-body text-navy/70">
                            {checkoutSuccess.product_type === 'ebook' && 'Your download is ready in your My Account section.'}
                            {checkoutSuccess.product_type === 'audiobook' && 'Your audiobook is ready — press play below to start listening.'}
                            {checkoutSuccess.product_type === 'physical' && 'Your physical book order is being processed.'}
                        </p>
                        {checkoutSuccess.product_type === 'audiobook' ? (
                            <button onClick={() => navigate(`/audiobook?product_id=${product.product_id}`)} className="bg-gold text-navy font-bold px-8 py-3 rounded-sm font-body transition-colors min-h-[44px]" data-testid="success-open-audiobook-btn">
                                Open Audiobook Player
                            </button>
                        ) : (
                            <button onClick={() => navigate('/')} className="bg-gold text-navy font-bold px-8 py-3 rounded-sm font-body transition-colors min-h-[44px]">
                                {checkoutSuccess.product_type === 'ebook' ? 'Go to My Downloads' : 'View My Orders'}
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

function AudiobookPreview({ productId }) {
    const [meta, setMeta] = React.useState(null);
    const [err, setErr] = React.useState('');
    const [playing, setPlaying] = React.useState(false);
    const [progress, setProgress] = React.useState(0);
    const audioRef = React.useRef(null);

    React.useEffect(() => {
        axios.get(`${API}/audiobook/${productId}/preview`)
            .then(r => setMeta(r.data))
            .catch(e => setErr(e.response?.data?.detail || 'Preview unavailable.'));
    }, [productId]);

    React.useEffect(() => {
        const a = audioRef.current;
        if (!a || !meta) return;
        const clampStart = () => {
            if (a.currentTime < meta.preview_start_sec) a.currentTime = meta.preview_start_sec;
        };
        const onLoaded = () => { a.currentTime = meta.preview_start_sec; };
        const onTime = () => {
            setProgress(Math.max(0, a.currentTime - meta.preview_start_sec));
            if (a.currentTime >= meta.preview_end_sec) { a.pause(); a.currentTime = meta.preview_start_sec; setPlaying(false); }
        };
        a.addEventListener('loadedmetadata', onLoaded);
        a.addEventListener('play', clampStart);
        a.addEventListener('timeupdate', onTime);
        return () => {
            a.removeEventListener('loadedmetadata', onLoaded);
            a.removeEventListener('play', clampStart);
            a.removeEventListener('timeupdate', onTime);
        };
    }, [meta]);

    if (err) return null;
    if (!meta) return (
        <div className="mb-6 bg-navy/5 border border-navy/10 rounded-lg px-4 py-3 font-body text-navy/60 text-sm" data-testid="audiobook-preview-loading">Loading audio preview…</div>
    );

    const total = Math.max(0, meta.preview_end_sec - meta.preview_start_sec);
    const pct = total ? Math.min(100, (progress / total) * 100) : 0;
    const fmt = s => { const m = Math.floor(s/60); const ss = Math.floor(s%60); return `${m}:${ss.toString().padStart(2,'0')}`; };

    const toggle = () => {
        const a = audioRef.current;
        if (!a) return;
        if (a.paused) { a.play(); setPlaying(true); } else { a.pause(); setPlaying(false); }
    };

    return (
        <div className="mb-6 bg-navy rounded-lg p-5 shadow-lg" data-testid="audiobook-preview-panel">
            <div className="flex items-center gap-4">
                <button onClick={toggle} className="bg-gold hover:bg-gold-hover text-navy rounded-full w-12 h-12 flex items-center justify-center flex-shrink-0 transition-colors" data-testid="audiobook-preview-toggle">
                    {playing ? <Pause size={22}/> : <Play size={22} className="ml-0.5"/>}
                </button>
                <div className="flex-1 min-w-0">
                    <p className="font-body text-gold uppercase tracking-wider text-[11px] font-bold mb-1">Listen to a preview</p>
                    <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                        <div className="h-full bg-gold transition-all" style={{width: `${pct}%`}} data-testid="audiobook-preview-progress"/>
                    </div>
                    <div className="flex justify-between font-body text-white/60 text-xs mt-1">
                        <span data-testid="audiobook-preview-elapsed">{fmt(progress)}</span>
                        <span data-testid="audiobook-preview-total">{fmt(total)}</span>
                    </div>
                </div>
            </div>
            <audio ref={audioRef} src={meta.audio_url} preload="metadata" data-testid="audiobook-preview-audio"/>
            <p className="font-body text-white/40 text-xs mt-3 italic">Preview only — buy the audiobook for the full narration.</p>
        </div>
    );
}
